import { createContext, useContext, useState, useEffect } from "react";

export const DEFAULT_A11Y = {
  font_size: "medium",
  high_contrast: false,
  colour_blind_mode: "none",
  reduce_motion: false,
  read_aloud: false,
  show_hovercards: true,
  underline_links: false,
};

export type A11ySettings = typeof DEFAULT_A11Y;

const AccessibilityContext = createContext({
  settings: DEFAULT_A11Y as A11ySettings,
  previewSettings: (_s: A11ySettings) => {},
  revertSettings: () => {},
  saveSettings: async (_s: A11ySettings) => {},
});

const FONT_MULTIPLIERS: Record<string, number> = {
  small:  0.85,
  medium: 1.0,
  large:  1.2,
  xlarge: 1.4,
};

const CB_FILTERS: Record<string, string> = {
  none:         "none",
  deuteranopia: "url(#deuteranopia)",
  protanopia:   "url(#protanopia)",
  tritanopia:   "url(#tritanopia)",
};

function injectSVGFilters() {
  if (document.getElementById("cb-svg-filters")) return;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.id = "cb-svg-filters";
  svg.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;";
  svg.innerHTML = `
    <defs>
      <filter id="deuteranopia">
        <feColorMatrix type="matrix" values="0.625 0.375 0 0 0  0.7 0.3 0 0 0  0 0.3 0.7 0 0  0 0 0 1 0"/>
      </filter>
      <filter id="protanopia">
        <feColorMatrix type="matrix" values="0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0"/>
      </filter>
      <filter id="tritanopia">
        <feColorMatrix type="matrix" values="0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0"/>
      </filter>
    </defs>`;
  document.body.appendChild(svg);
}

export function applySettings(settings: A11ySettings) {
  injectSVGFilters();

  const root = document.documentElement;

  // Font size
  const existingFont = document.getElementById("a11y-font-style");
  if (existingFont) existingFont.remove();

  if (settings.font_size !== "medium") {
    const m = FONT_MULTIPLIERS[settings.font_size] ?? 1.0;
    const style = document.createElement("style");
    style.id = "a11y-font-style";
    style.textContent = `
      p, span, div, a, button, input, select, textarea,
      label, td, th, li, h1, h2, h3, h4, h5, h6 {
        font-size: calc(1rem * ${m}) !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Body filter — high contrast takes priority over colour-blind
  if (settings.high_contrast) {
    document.body.style.filter = "contrast(1.5)";
    root.setAttribute("data-high-contrast", "true");
  } else if (settings.colour_blind_mode !== "none") {
    document.body.style.filter = CB_FILTERS[settings.colour_blind_mode] ?? "none";
    root.setAttribute("data-high-contrast", "false");
  } else {
    document.body.style.filter = "none";
    root.setAttribute("data-high-contrast", "false");
  }

  // Reduce motion
  let motionStyle = document.getElementById("a11y-motion-style");
  if (settings.reduce_motion) {
    if (!motionStyle) {
      motionStyle = document.createElement("style");
      motionStyle.id = "a11y-motion-style";
      document.head.appendChild(motionStyle);
    }
    motionStyle.textContent = `
      *, *::before, *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
        scroll-behavior: auto !important;
      }
    `;
  } else {
    if (motionStyle) motionStyle.remove();
  }

  // Underline links
  let linkStyle = document.getElementById("a11y-link-style");
  if (settings.underline_links) {
    if (!linkStyle) {
      linkStyle = document.createElement("style");
      linkStyle.id = "a11y-link-style";
      document.head.appendChild(linkStyle);
    }
    linkStyle.textContent = "a, a * { text-decoration: underline !important; }";
  } else {
    if (linkStyle) linkStyle.remove();
  }

  // Hovercards
  let hoverStyle = document.getElementById("a11y-hover-style");
  if (!settings.show_hovercards) {
    if (!hoverStyle) {
      hoverStyle = document.createElement("style");
      hoverStyle.id = "a11y-hover-style";
      document.head.appendChild(hoverStyle);
    }
    hoverStyle.textContent = `
      [title]:hover::after { display: none !important; }
      [data-tooltip] { display: none !important; }
      .tooltip, .tooltiptext { display: none !important; visibility: hidden !important; }
    `;
  } else {
    if (hoverStyle) hoverStyle.remove();
  }

  // Read aloud
  const win = window as unknown as Record<string, unknown>;
  const existing = win._a11yReadAloud as EventListenerOrEventListenerObject | undefined;
  if (existing) {
    document.removeEventListener("mouseup", existing);
    delete win._a11yReadAloud;
    window.speechSynthesis?.cancel();
  }

  if (settings.read_aloud) {
    const handler = () => {
      const selected = window.getSelection()?.toString().trim() ?? "";
      if (selected.length > 0 && selected.length < 500) {
        window.speechSynthesis?.cancel();
        const utt = new SpeechSynthesisUtterance(selected);
        utt.lang  = "en-IN";
        utt.rate  = 0.85;
        utt.pitch = 1;
        window.speechSynthesis?.speak(utt);
      }
    };
    document.addEventListener("mouseup", handler);
    win._a11yReadAloud = handler;
  }

  root.setAttribute("data-reduce-motion", settings.reduce_motion ? "true" : "false");
  root.setAttribute("data-font-size", settings.font_size);
}

function loadSavedSettings(): A11ySettings {
  const raw = localStorage.getItem("accessibility");
  if (raw) {
    try { return { ...DEFAULT_A11Y, ...JSON.parse(raw) }; } catch { /* ignore */ }
  }
  return DEFAULT_A11Y;
}

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<A11ySettings>(DEFAULT_A11Y);

  useEffect(() => {
    const saved = loadSavedSettings();
    setSettings(saved);
    applySettings(saved);

    const token = localStorage.getItem("token");
    if (token) {
      fetch("/api/accessibility/settings", {
        headers: { Authorization: "Bearer " + token },
      })
        .then(r => r.json())
        .then(data => {
          if (data && !data.detail) {
            const merged = { ...DEFAULT_A11Y, ...data };
            setSettings(merged);
            applySettings(merged);
            localStorage.setItem("accessibility", JSON.stringify(merged));
          }
        })
        .catch(() => {});
    }
  }, []);

  // Visual-only preview — does NOT touch localStorage or settings state
  const previewSettings = (s: A11ySettings) => {
    applySettings(s);
  };

  // Revert DOM to the last saved state (reads from localStorage)
  const revertSettings = () => {
    const saved = loadSavedSettings();
    setSettings(saved);
    applySettings(saved);
  };

  const saveSettings = async (newSettings: A11ySettings): Promise<void> => {
    setSettings(newSettings);
    applySettings(newSettings);
    localStorage.setItem("accessibility", JSON.stringify(newSettings));
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await fetch("/api/accessibility/settings", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(newSettings),
        });
      } catch {
        // silently ignore
      }
    }
  };

  return (
    <AccessibilityContext.Provider value={{ settings, previewSettings, revertSettings, saveSettings }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export const useAccessibility = () => useContext(AccessibilityContext);
